import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { finalize } from 'rxjs';
import { TitleBarComponent } from '../../shared/components/title-bar/title-bar.component';
import { ConfirmActionModalComponent } from '../../shared/components/confirm-action-modal';
import { NotificationService } from '../../services/notification.service';
import { FeatureFlagsService } from '../services/feature-flags.service';
import { FlagSummary, GlobalOnChoice } from '../models/feature-flag.model';
import {
  FEATURE_FLAG_PROPAGATION_NOTE,
  buildGlobalPayload,
  descriptionLines,
  globalConfirmTitle,
  globalOffMessage,
  onOffLabel,
} from '../utils/feature-flags.util';

interface PendingToggle {
  flag: FlagSummary;
  turnOn: boolean;
}

/**
 * CWS-1805: super-admin page listing every feature flag with its global switch
 * and company default. Turning a flag on or off always goes through a confirm.
 */
@Component({
  selector: 'app-admin-feature-flags',
  standalone: true,
  imports: [CommonModule, MatSlideToggleModule, TitleBarComponent, ConfirmActionModalComponent],
  templateUrl: './admin-feature-flags.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./admin-feature-flags.component.scss'],
})
export class AdminFeatureFlagsComponent implements OnInit {
  private readonly service = inject(FeatureFlagsService);
  private readonly notifications = inject(NotificationService);

  readonly propagationNote = FEATURE_FLAG_PROPAGATION_NOTE;
  readonly offMessage = globalOffMessage();
  readonly onOffLabel = onOffLabel;
  readonly linesOf = descriptionLines;

  flags: FlagSummary[] = [];
  loading = false;
  saving = false;
  errorMessage = '';
  pending: PendingToggle | null = null;
  choice: GlobalOnChoice = 'all';

  ngOnInit(): void {
    this.load();
  }

  get dialogTitle(): string {
    return this.pending ? globalConfirmTitle(this.pending.flag, this.pending.turnOn) : '';
  }

  load(): void {
    this.loading = true;
    this.errorMessage = '';
    this.service
      .listFlags()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: res => {
          if (res.status === false) {
            this.errorMessage = res.message || 'Could not load feature flags.';
            return;
          }
          this.flags = res.data ?? [];
        },
        error: err => {
          this.errorMessage = err?.error?.message || 'Could not load feature flags.';
        },
      });
  }

  onToggleChange(event: MatSlideToggleChange, flag: FlagSummary): void {
    // The switch is put back at once and only flips once the confirm is accepted and saved.
    event.source.checked = flag.global_enabled;
    if (this.saving) {
      return;
    }
    this.choice = 'all';
    this.pending = { flag, turnOn: !flag.global_enabled };
  }

  setChoice(choice: GlobalOnChoice): void {
    this.choice = choice;
  }

  cancelDialog(): void {
    this.pending = null;
  }

  confirmDialog(): void {
    if (!this.pending || this.saving) {
      return;
    }
    const { flag, turnOn } = this.pending;
    this.saving = true;
    this.service
      .updateFlag(flag.key, buildGlobalPayload(turnOn, this.choice))
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: res => {
          if (res.status === false) {
            this.notifyError(res.message);
            return;
          }
          this.flags = res.data ?? this.flags;
          this.pending = null;
        },
        error: err => {
          this.notifyError(err?.error?.message);
        },
      });
  }

  trackFlag(_index: number, flag: FlagSummary): string {
    return flag.key;
  }

  private notifyError(message?: string): void {
    this.notifications.add({
      severity: 'error',
      summary: 'Save failed',
      detail: message || 'Could not update the feature flag.',
    });
  }
}
